import { AddIcon, CopyIcon } from "@chakra-ui/icons"
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Tr,
  VStack,
} from "@chakra-ui/react"
import { Api } from "../lib/api"
import React from "react"
import Link from "./Link"
import { FaDownload } from "react-icons/fa6"
import { addAlert, AlertType } from "@/lib/alerts"

const Identities = () => {
  const api = new Api()
  const [hasStatus, setHasStatus] = React.useState(false)
  const [identities, setIdentities] = React.useState([])

  const getIdentities = async () => {
    try {
      const identitiesList = await api.identitiesList()
      setIdentities(identitiesList)
    } catch (error) {
      console.error("Error fetching identities:", error)
      addAlert(AlertType.ERROR, "Error fetching identities", error)
    }
  }

  React.useEffect(() => {
    const getStatus = async () => {
      return await api.statusRetrieve()
    }

    const fetchData = async () => {
      const status = await getStatus()
      setHasStatus(status)
      await getIdentities()
    }
    fetchData()
  }, [])

  const hasProxy = React.useMemo(() => {
    if (!hasStatus) return
    return hasStatus.behind_proxy
  }, [hasStatus])

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
  }

  const handleCreateIdentity = async () => {
    try {
      const identity = await api.identitiesCreate()
      setIdentities([...identities, identity])
      addAlert(
        AlertType.SUCCESS,
        "Identity created",
        "Identity created successfully",
      )
      getIdentities()
    } catch (error) {
      const err = await error.json()
      console.log("Error creating identity", err)
      addAlert(AlertType.ERROR, "Error creating identity", err.error)
    }
  }

  const identityList = () => {
    return (
      <>
        {Array.isArray(identities) && identities.length > 0 && (
          <TableContainer
            boxShadow="md"
            borderRadius="0.75em"
            height="100%"
            bg="white"
          >
            <Table>
              <thead>
                <tr>
                  <Th fontWeight="bold">ID</Th>
                  <Th fontWeight="bold">Device ID</Th>
                  <Th fontWeight="bold">Public Key</Th>
                </tr>
              </thead>
              <Tbody>
                {identities.map((identity) => (
                  <Tr key={identity.id}>
                    <Td>
                      <div>
                        <span>{identity.id}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopy(identity.id)}
                          aria-label="Copy ID"
                        >
                          <CopyIcon />
                        </Button>
                      </div>
                    </Td>
                    <Td>
                      <div>
                        <span>{identity.device_id}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopy(identity.device_id)}
                          aria-label="Copy Device ID"
                        >
                          <CopyIcon />
                        </Button>
                      </div>
                    </Td>
                    <Td>
                      <div>
                        <span>{identity.primary_public_key}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopy(identity.public_key)}
                          aria-label="Copy Public Key"
                        >
                          <CopyIcon />
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </>
    )
  }

  const hasProxyNoIdentities = () => {
    if (identities.length === 0)
      return (
        <>
          <Alert status="info" position="relative" width="100%" height="auto">
            <VStack
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <HStack>
                <AlertIcon />
                <Heading size="sm" mt={0} mb={0}>
                  No Identities
                </Heading>
              </HStack>
              <Text width="100%" whiteSpace="normal" overflowWrap="break-word">
                Click the Add Identity button to create a new Identity.
              </Text>
            </VStack>
          </Alert>
        </>
      )
    return
  }

  const noProxyHasIdentities = () => {
    if (identities.length > 0)
      return (
        <>
          <Alert
            status="info"
            position="relative"
            width="100%"
            height="auto"
            padding="0.5em"
          >
            <HStack
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <HStack>
                <AlertIcon />
                <Heading size="sm" mt={0} mb={0}>
                  No Proxy Detected
                </Heading>
                <Text whiteSpace="normal" overflowWrap="break-word">
                  Download the Blind Proxy to create identities.
                </Text>
              </HStack>
              <Link
                href="https://docs.blindinsight.io/download/"
                isExternal
                rel="noreferrer"
              >
                <Button
                  variant="primary"
                  title="Download the Blind Proxy"
                  size={{ base: "sm", md: "md" }}
                  rightIcon={<FaDownload />}
                >
                  Download the Blind Proxy
                </Button>
              </Link>
            </HStack>
          </Alert>
        </>
      )
    return
  }

  const noProxyEmpty = () => {
    return (
      <>
        <Alert status="info" position="relative" width="100%" height="auto">
          <VStack
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <HStack>
              <AlertIcon />
              <Heading size="md" mt={0} mb={0}>
                No Proxy Detected
              </Heading>
            </HStack>
            <VStack>
              <Text
                variant="base"
                width="100%"
                whiteSpace="normal"
                overflowWrap="break-word"
              >
                Only encrypted data ever enters or exits Blind Insight, and we
                don&apos;t hold any keys. <br></br>Download the Blind Proxy to
                create identities. Soon, the Blind Proxy will be available as a
                browser extension, mobile library, and via secure enclave.
              </Text>
            </VStack>
            <Flex
              width="full"
              direction={{ base: "column", md: "row" }}
              justifyContent="right"
              gap={{ base: "1", md: "4" }}
              flexWrap="wrap"
            >
              <Link
                href="https://docs.blindinsight.io/download/"
                isExternal
                rel="noreferrer"
              >
                <Button
                  variant="primary"
                  title="Download the Blind Proxy"
                  size={{ base: "sm", md: "md" }}
                  rightIcon={<FaDownload />}
                >
                  Download the Blind Proxy
                </Button>
              </Link>
            </Flex>
          </VStack>
        </Alert>
      </>
    )
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      gap="1em"
      bg="Gray.100"
      p={4}
      borderRadius="1em"
      height={{ base: "50%", md: "100%" }}
    >
      <HStack
        justifyContent="space-between"
        alignItems={
          hasProxy || (!hasProxy && identities.length > 0)
            ? "center"
            : "flex-start"
        }
      >
        {hasProxy && identities.length === 0 ? (
          hasProxyNoIdentities()
        ) : !hasProxy && identities.length > 0 ? (
          noProxyHasIdentities()
        ) : (
          <Box flex="1"></Box>
        )}
        <Button
          aria-label={
            hasProxy
              ? "Create a new Identity"
              : "You must be behind the proxy to create a new Identity"
          }
          disabled={!hasProxy}
          rightIcon={<AddIcon />}
          variant="primary"
          onClick={handleCreateIdentity}
          flexShrink={0}
          minW="fit-content"
        >
          Create Identity
        </Button>
      </HStack>
      {!hasProxy && identities.length === 0 ? noProxyEmpty() : <></>}
      {identityList()}
    </Box>
  )
}

export default Identities
